import * as React from "react";
import {
  Outlet,
  createRootRouteWithContext,
  useLocation,
  useNavigate,
} from "@tanstack/react-router";
import { Toaster } from "@/components/ui/sonner";
import { QueryClient } from "@tanstack/react-query";
import { env } from "@/env";
import { RootErrorComponent } from "@/components/root-error";
import { datadogLogs } from "@datadog/browser-logs";
import { datadogRum, RumErrorEvent } from "@datadog/browser-rum";
import { PostHogErrorBoundary, PostHogProvider } from "posthog-js/react";
import type { PostHogInterface } from "posthog-js";

import { isDatadogNoise } from "@/lib/datadog-noise";
import { DATADOG_SERVICE, DATADOG_SITE, datadogEnvironment } from "@/lib/utils";

interface MyRouterContext {
  queryClient: QueryClient;
}

declare global {
  interface Window {
    posthog?: PostHogInterface;
  }
}

export const Route = createRootRouteWithContext<MyRouterContext>()({
  component: RootComponent,
  errorComponent: (props) => <RootErrorComponent errorProps={props} />,
  notFoundComponent: () => <NotFoundComponent />,
});

/**
 * If using the legact dashboard /dashboard, redirect to /payments/payin of the new dashboard default route
 */
const NotFoundComponent = () => {
  const location = useLocation();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (location.pathname === "/dashboard") {
      navigate({ to: "/payments/payin" });
    }
  }, [location]);
  if (location.pathname === "/dashboard") {
    return null;
  }

  return <div>Not found</div>;
};

const isLocalhost = window.location.hostname === "localhost";
const isLiveMerchantPortal =
  env.VITE_VOLUME_URL === "https://api.volumepay.io" &&
  import.meta.env.VITE_IS_PLAYWRIGHT !== "true";
const isPostHogEnabled = env.VITE_PUBLIC_POSTHOG_KEY.trim().length > 0;
const isPostHogErrorTrackingEnabled = isLiveMerchantPortal && isPostHogEnabled;
const isLocalPostHogToolbarEnabled =
  import.meta.env.DEV && import.meta.env.VITE_IS_PLAYWRIGHT !== "true";
const postHogApiHost = import.meta.env.DEV
  ? new URL(env.VITE_PUBLIC_POSTHOG_HOST, window.location.origin).toString()
  : env.VITE_PUBLIC_POSTHOG_HOST;
const postHogUiHost = import.meta.env.DEV
  ? "https://eu.posthog.com"
  : env.VITE_PUBLIC_POSTHOG_HOST;
const postHogExceptionCapture = {
  capture_unhandled_errors: true,
  capture_unhandled_rejections: true,
  capture_console_errors: true,
};

function configureLocalPostHog(posthog: PostHogInterface) {
  if (isLocalhost) {
    posthog.opt_out_capturing();
  }
  if (isLocalPostHogToolbarEnabled) {
    window.posthog = posthog;
    posthog.loadToolbar({
      token: env.VITE_PUBLIC_POSTHOG_KEY,
      toolbarVersion: "toolbar",
      instrument: true,
      userIntent: "add-action",
      dataAttributes: ["data-attr"],
    });
  }
}

const TanStackRouterDevtools =
  !isLocalhost &&
  (env.VITE_VOLUME_URL === "https://api.volumepay.io" ||
    env.VITE_VOLUME_URL === "https://api.sandbox.volumepay.io" ||
    env.VITE_IS_DEMO)
    ? () => null // Render nothing in production
    : React.lazy(() =>
        // Lazy load in development
        import("@tanstack/react-router-devtools").then((res) => ({
          default: res.TanStackRouterDevtools,
          // For Embedded Mode
          // default: res.TanStackRouterDevtoolsPanel
        })),
      );

const ReactQueryDevtools =
  !isLocalhost &&
  (env.VITE_VOLUME_URL === "https://api.volumepay.io" ||
    env.VITE_VOLUME_URL === "https://api.sandbox.volumepay.io" ||
    env.VITE_IS_DEMO)
    ? () => null // Render nothing in production
    : React.lazy(() =>
        // Lazy load in development
        import("@tanstack/react-query-devtools").then((res) => ({
          default: res.ReactQueryDevtools,
        })),
      );

if (!isLocalhost) {
  if (import.meta.env.VITE_IS_PLAYWRIGHT !== "true") {
    datadogLogs.init({
      clientToken: env.VITE_DATADOG_CLIENT_TOKEN,
      site: DATADOG_SITE,
      service: DATADOG_SERVICE,
      forwardErrorsToLogs: true,
      sessionSampleRate: 100,
      env: datadogEnvironment(),
      beforeSend(event) {
        return !isDatadogNoise(
          [{ message: event.message }, event.error ?? {}],
          window.location.origin,
        );
      },
    });

    datadogRum.init({
      applicationId: env.VITE_DATADOG_RUM_APPLICATION_ID,
      clientToken: env.VITE_DATADOG_CLIENT_TOKEN,
      site: DATADOG_SITE,
      service: DATADOG_SERVICE,
      env: datadogEnvironment(),
      allowedTracingUrls: [/https:\/\/.*\.volumepay\.io/],
      version: import.meta.env.VITE_VERCEL_GIT_COMMIT_SHA ?? "local",
      sessionSampleRate: 100,
      sessionReplaySampleRate: env.VITE_IS_LOCAL ? 0 : 20,
      trackUserInteractions: !env.VITE_IS_LOCAL,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: "mask-user-input",
      beforeSend(event) {
        if (event.type !== "error") {
          return true;
        }
        const { error } = event as RumErrorEvent;
        return !isDatadogNoise(
          [error, ...(error.causes ?? [])],
          window.location.origin,
        );
      },
    });
  }
}

function RootComponent() {
  const app = (
    <>
      <Toaster richColors />
      <Outlet />
      <ReactQueryDevtools initialIsOpen={false} />
      <TanStackRouterDevtools position="bottom-left" />
    </>
  );

  if (!isPostHogEnabled) {
    return app;
  }

  const appWithErrorBoundary = isPostHogErrorTrackingEnabled ? (
    <PostHogErrorBoundary
      fallback={<RootErrorComponent captureInPostHog={false} />}
      additionalProperties={(error) => ({
        app: "merchant-portal",
        release: import.meta.env.VITE_VERCEL_GIT_COMMIT_SHA ?? "local",
        errorName: error instanceof Error ? error.name : undefined,
      })}
    >
      {app}
    </PostHogErrorBoundary>
  ) : (
    app
  );

  return (
    <PostHogProvider
      apiKey={env.VITE_PUBLIC_POSTHOG_KEY}
      options={{
        // The toolbar requires an absolute API host; keep using the same-origin
        // development proxy rather than sending ingestion directly to PostHog.
        api_host: postHogApiHost,
        // The local ingestion proxy does not serve PostHog's toolbar/editor UI.
        ui_host: postHogUiHost,
        defaults: "2025-05-24",
        autocapture: false,
        capture_dead_clicks: false,
        capture_heatmaps: import.meta.env.DEV ? false : undefined,
        capture_pageleave: false,
        disable_session_recording: isLocalhost,
        disable_surveys: isLocalhost,
        opt_out_capturing_by_default: isLocalhost,
        ...(isLocalhost || isLocalPostHogToolbarEnabled
          ? {
              loaded: configureLocalPostHog,
            }
          : {}),
        ...(import.meta.env.VITE_IS_PLAYWRIGHT === "true"
          ? { disable_external_dependency_loading: true }
          : {}),
        ...(isPostHogErrorTrackingEnabled
          ? { capture_exceptions: postHogExceptionCapture }
          : {}),
        rageclick: false,
      }}
    >
      {appWithErrorBoundary}
    </PostHogProvider>
  );
}
